import * as SwitchPrimitive from '@radix-ui/react-switch';
import * as React from 'react';

import { cn } from '@elara/client-core/lib/utils';

/**
 * A Radix switch. With `isBusy` (e.g. while the change is saved on the server) a spinner turns inside
 * the handle and the switch ignores input, without the faded disabled look.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root> & { isBusy?: boolean }
>(({ className, isBusy = false, disabled, ...props }, ref) => (
  <SwitchPrimitive.Root
    className={cn(
      'peer inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      isBusy && 'disabled:cursor-wait disabled:opacity-100',
      className,
    )}
    disabled={disabled || isBusy}
    aria-busy={isBusy || undefined}
    {...props}
    ref={ref}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        'pointer-events-none flex items-center justify-center h-5 w-5 rounded-full bg-background shadow-lg ring-0 transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
      )}
    >
      {isBusy && (
        <span
          data-loading-indicator="spinner"
          className="h-3 w-3 animate-spin rounded-full border-2 border-accent border-r-transparent"
        />
      )}
    </SwitchPrimitive.Thumb>
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
